import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { logCrud } from "@/services/activities";

const permissionSchema = z.object({
  name: z.string().min(3, "Nome deve ter no mínimo 3 caracteres"),
  module: z.string().min(1, "Selecione um módulo"),
  action: z.string().min(1, "Selecione uma ação"),
  description: z.string().optional(),
});

type PermissionFormData = z.infer<typeof permissionSchema>;

interface Permission {
  id: string;
  name: string;
  module: string;
  action: string;
  slug: string;
  description: string | null;
}

interface PermissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  permission?: Permission;
}

const modules = [
  { value: "products", label: "Produtos" },
  { value: "clients", label: "Clientes" },
  { value: "suppliers", label: "Fornecedores" },
  { value: "transactions", label: "Transações" },
  { value: "users", label: "Usuários" },
  { value: "roles", label: "Perfis" },
  { value: "permissions", label: "Permissões" },
  { value: "reports", label: "Relatórios" },
  { value: "dashboard", label: "Dashboard" },
  { value: "settings", label: "Configurações" },
  { value: "tenants", label: "Tenants" },
];

const actions = [
  { value: "create", label: "Criar" },
  { value: "read", label: "Visualizar" },
  { value: "update", label: "Editar" },
  { value: "delete", label: "Excluir" },
  { value: "manage", label: "Gerenciar" },
  { value: "view", label: "Ver" },
  { value: "export", label: "Exportar" },
];

export function PermissionModal({ isOpen, onClose, permission }: PermissionModalProps) {
  const queryClient = useQueryClient();
  const isEditing = !!permission;

  const form = useForm<PermissionFormData>({
    resolver: zodResolver(permissionSchema),
    defaultValues: {
      name: "",
      module: "",
      action: "",
      description: "",
    },
  });

  useEffect(() => {
    if (permission) {
      form.reset({
        name: permission.name,
        module: permission.module,
        action: permission.action,
        description: permission.description || "",
      });
    } else {
      form.reset({
        name: "",
        module: "",
        action: "",
        description: "",
      });
    }
  }, [permission, form]);

  const mutation = useMutation({
    mutationFn: async (data: PermissionFormData) => {
      const slug = `${data.module}.${data.action}`;

      if (isEditing) {
        const { error } = await supabase
          .from("permissions" as any)
          .update({
            name: data.name,
            module: data.module,
            action: data.action,
            slug,
            description: data.description || null,
          } as any)
          .eq("id", permission.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("permissions" as any)
          .insert({
            name: data.name,
            module: data.module,
            action: data.action,
            slug,
            description: data.description || null,
          } as any);

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["permissions"] });
      toast.success(isEditing ? "Permissão atualizada!" : "Permissão criada!");
      const slug = `${form.getValues("module")}.${form.getValues("action")}`;
      if (isEditing && permission?.id) {
        void logCrud({ entitySlug: "entity:permissions", operationSlug: "update", entityId: permission.id, entityName: slug, screenSlug: "permissoes", before: (permission as any), after: { module: form.getValues('module'), action: form.getValues('action') } });
      } else {
        void logCrud({ entitySlug: "entity:permissions", operationSlug: "create", entityName: slug, screenSlug: "permissoes" });
      }
      form.reset();
      onClose();
    },
    onError: (error: any) => {
      console.error("Erro ao salvar permissão:", error);
      toast.error(
        error.message?.includes("duplicate")
          ? "Já existe uma permissão com este módulo e ação"
          : "Erro ao salvar permissão"
      );
    },
  });

  const onSubmit = (data: PermissionFormData) => {
    mutation.mutate(data);
  };

  const currentSlug = form.watch("module") && form.watch("action")
    ? `${form.watch("module")}.${form.watch("action")}`
    : "";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Editar Permissão" : "Nova Permissão"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Atualize os dados da permissão"
              : "Crie uma nova permissão para o sistema"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="module"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Módulo</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o módulo" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {modules.map((module) => (
                        <SelectItem key={module.value} value={module.value}>
                          {module.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="action"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ação</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a ação" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {actions.map((action) => (
                        <SelectItem key={action.value} value={action.value}>
                          {action.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {currentSlug && (
              <div className="rounded-md bg-muted p-3">
                <p className="text-sm text-muted-foreground mb-1">Slug gerado:</p>
                <code className="text-sm font-mono">{currentSlug}</code>
              </div>
            )}

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: Criar Produto" {...field} />
                  </FormControl>
                  <FormDescription>
                    Nome descritivo da permissão
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição (Opcional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Descreva o que esta permissão permite fazer..."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
